import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import GetTrack from '@App/api/query/GetTrack.graphql';
import Query from '@App/components/api/Query';
import Player from '@App/components/player/Player';
import { SIZE_LG } from '@App/service/MobileWatcher';
import { pause, play, playTrack, playSoundEffect } from '@App/store/actions/player';
import { getNextTrack, getPreviousTrack } from '@App/store/reducers/player';
import { trackPlayed } from '@App/store/actions/stats';
import Highlight from '@App/model/similarSearch/Highlight';
import { RootState } from '@App/store';

interface OwnProps {
  dispatchTrackPlayedStat?: boolean
  hasStreamingFeature?: boolean
  modeLoop?: boolean
}

function mapResult(props, data) {
  const { authenticated, trackReference, versionReference } = props;
  const { track } = data;
  const { album, versions } = track;
  const version = getVersion(versions, versionReference);

  if (!version) {
    throw new Error(`Could not find version for track "${trackReference}" and version "${versionReference}".`);
  }

  return {
    title: track.title,
    waveform: version.waveform,
    duration: version.duration,
    audioUrl: authenticated ? version.audioFileUrl : version.previewAudioFileUrl,
    cover: album.coverAsThumbnail,
    nbVersions: versions.length,
    versionReference: version.reference,
    albumReference: album.reference,
    loading: false,
    adminEditUrl: version.adminEditUrl,
  };
}

function getVersion(versions, versionReference: string | null = null) {
  if (versionReference === null) {
    return versions.find(version => version.main);
  }

  return versions.find(version => version.reference === versionReference);
}

/**
 * GraphQL wrapped Player
 *
 * @param {Object} props
 */
function PlayerWrapper(props) {
  const {
    trackReference,
    versionReference,
    startPosition,
    highlights,
    soundEffect,
    mobile,
    reduced,
    authenticated,
    playTrack,
    playSoundEffect,
    previousTrack,
    nextTrack,
    onPlay,
    onPause,
    modeLoop,
    hasStreamingFeature,
  } = props;

  if (!trackReference) {
    return null;
  }

  const childProps = {
    trackReference,
    versionReference,
    startPosition,
    highlights,
    mobile,
    reduced,
    authenticated,
    playTrack,
    playSoundEffect,
    previousTrack,
    nextTrack,
    onPlay,
    onPause,
    modeLoop,
    hasStreamingFeature,
  };

  // Sound effect: play the signed preview URL directly, short-circuiting the GetTrack query.
  // All display metadata comes from the redux store (no server round-trip).
  if (soundEffect) {
    return <Player
      {...childProps}
      loading={false}
      soundEffect={soundEffect}
      title={soundEffect.title}
      duration={soundEffect.duration}
      audioUrl={soundEffect.previewUrl}
      // No inline peaks: a sound effect's waveform is fetched from `waveformDataUrl`, which the
      // player bar reads off `soundEffect` (see Player.renderPlayBar / PlayerStatus).
      waveform={[]}
      cover={soundEffect.artworkUrl ?? null}
      nbVersions={0}
      availableForSimilarSearch={false}
    />;
  }

  return <Query
    component={Player}
    loadingComponent={Player}
    query={GetTrack}
    variables={{ reference: trackReference }}
    mapResult={data => mapResult(props, data)}
    mapLoading={() => ({ loading: true })}
    childProps={childProps}
  />;
}

PlayerWrapper.propTypes = {
  authenticated: PropTypes.bool.isRequired,
  mobile: PropTypes.bool.isRequired,
  reduced: PropTypes.bool.isRequired,
  playTrack: PropTypes.func.isRequired,
  playSoundEffect: PropTypes.func.isRequired,
  trackReference: PropTypes.string,
  versionReference: PropTypes.string,
  startPosition: PropTypes.number,
  highlights: PropTypes.arrayOf(Highlight),
  soundEffect: PropTypes.shape({
    id: PropTypes.string.isRequired,
    previewUrl: PropTypes.string,
    title: PropTypes.string,
    duration: PropTypes.number,
    waveformDataUrl: PropTypes.string,
    waveformImageUrl: PropTypes.string,
    artworkUrl: PropTypes.string,
  }),
  previousTrack: PropTypes.shape({
    trackReference: PropTypes.string.isRequired,
    versionReference: PropTypes.string,
    highlights: PropTypes.arrayOf(Highlight),
    soundEffect: PropTypes.object,
  }),
  nextTrack: PropTypes.shape({
    trackReference: PropTypes.string.isRequired,
    versionReference: PropTypes.string,
    highlights: PropTypes.arrayOf(Highlight),
    soundEffect: PropTypes.object,
  }),
  onPlay: PropTypes.func.isRequired,
  onPause: PropTypes.func.isRequired,
  modeLoop: PropTypes.bool.isRequired,
  hasStreamingFeature: PropTypes.bool.isRequired,
};

PlayerWrapper.defaultProps = {
  trackReference: null,
  versionReference: null,
  highlights: [],
  soundEffect: null,
  nextTrack: null,
  previousTrack: null,
  modeLoop: false,
  hasStreamingFeature: false,
};

/**
 * Redux drived Player
 */
const PlayerContainer = connect(
  (state: RootState) => ({
    authenticated: state.authentication.id !== null,
    mobile: (state.navigation as { size: number }).size < SIZE_LG,
    reduced: (state.navigation as { size: number }).size < SIZE_LG,
    trackReference: state.player.trackReference,
    versionReference: state.player.versionReference,
    startPosition: state.player.startPosition,
    highlights: state.player.highlights,
    soundEffect: state.player.soundEffect,
    previousTrack: getPreviousTrack(state),
    nextTrack: getNextTrack(state),
  }),
  (dispatch, props: OwnProps) => ({
    playTrack: (trackReference, versionReference, startPosition = 0, highlights = []) => {
      if (props.dispatchTrackPlayedStat) {
        dispatch(trackPlayed(versionReference, props.hasStreamingFeature));
      }
      dispatch(playTrack(trackReference, versionReference, startPosition, highlights));
    },
    playSoundEffect: (soundEffect) => dispatch(playSoundEffect(soundEffect)),
    onPlay: () => dispatch(play()),
    onPause: () => dispatch(pause()),
  }),
)(PlayerWrapper);

(PlayerContainer as unknown as { propTypes: object }).propTypes = {
  dispatchTrackPlayedStat: PropTypes.bool,
  modeLoop: PropTypes.bool,
  hasStreamingFeature: PropTypes.bool,
};

export default connect((state: RootState) => ({
  dispatchTrackPlayedStat: !state.authentication.permissions.isAdmin && !state.authentication.permissions.isStaff,
  modeLoop: state.player.modeLoop,
  hasStreamingFeature: state.authentication?.permissions?.hasStreamingFeature === true,
}))(PlayerContainer);
